import { z } from 'zod';
import snapshot from '@/data/stations-snapshot.json';
import type { Station } from '@/domain/geo';

export type StationsResult = { stations: Station[]; source: 'live' | 'snapshot' };

/* The endpoint quotes coordinates as strings today. Accept a real number too, so a
   backend change that stops quoting them does not blank the map. `Number('')` is 0,
   which is a valid point in the Gulf of Guinea, so a blank string has to be rejected
   before the coercion rather than after. */
const coordinateSchema = z.union([
  z.number().refine((value) => Number.isFinite(value)),
  z
    .string()
    .refine((value) => value.trim() !== '')
    .transform((value) => Number(value))
    .refine((value) => Number.isFinite(value)),
]);

/* A station without usable coordinates cannot be measured or drawn, so it is dropped.
   A missing address only costs a label, so it falls back to ''. */
const stationSchema = z
  .object({
    latitude: coordinateSchema,
    longitude: coordinateSchema,
    address: z.string().catch(''),
    is_open: z.boolean().catch(false),
  })
  .transform(
    (entry): Station => ({
      lat: entry.latitude,
      lon: entry.longitude,
      address: entry.address,
      isOpen: entry.is_open,
    }),
  );

// The live payload and the bundled snapshot share one shape, so they share one schema.
const stationsSchema = z.array(z.unknown()).transform((entries) =>
  entries.flatMap((entry) => {
    const parsed = stationSchema.safeParse(entry);
    return parsed.success ? [parsed.data] : [];
  }),
);

export const parseStations = (body: unknown): Station[] => {
  const parsed = stationsSchema.safeParse(body);
  return parsed.success ? parsed.data : [];
};

const snapshotStations = parseStations(snapshot);

const snapshotResult = (): StationsResult => ({
  stations: [...snapshotStations],
  source: 'snapshot',
});

export const fetchStations = async (
  url: string,
  fetchImpl: typeof fetch = fetch,
): Promise<StationsResult> => {
  try {
    const response = await fetchImpl(url);
    if (!response.ok) {
      return snapshotResult();
    }
    const stations = parseStations(await response.json());
    if (stations.length === 0) {
      return snapshotResult();
    }
    return { stations, source: 'live' };
  } catch {
    return snapshotResult();
  }
};
